import { Skeleton } from 'antd';
import { useInjection } from 'inversify-react';
import { useState } from 'react';

import { Panel, Text } from '@desygner/ui-common-components';
import { useTranslation } from '@desygner/ui-common-translation';
import { useProjectsStore } from '@desygner/ui-features-projects';

import { IProjectManager } from '../../../../core/viewer/project-manager';
import { TYPES } from '../../../../inversify.config';
import { IViewerStoreInjector } from '../../../../store';
import { ProjectAction } from '../../../../store/viewer/viewer.types';
import { useResponsive } from '../../../hooks/useResponsive';
import { Logo } from '../../Logo';
import { ThemeModeSwitch } from '../../ThemeModeSwitch/ThemeModeSwitch';
import { StyledFlexBox } from '../BottomNav/BottomNav.styles';
import { StyledTopNav } from './TopNav.styles';
import { AdminApprovalContainer } from './components/AdminApprovalContainer';
import { BottomSheetMenu } from './components/BottomSheetMenu/BottomSheetMenu';
import { ProjectActions } from './components/ProjectActions/ProjectActions';
import { Share } from './components/Share/Share';

export type TopNavProps = {
  visible: boolean;
};

export const TopNav = ({ visible }: TopNavProps) => {
  const { t } = useTranslation();
  const projectManager = useInjection<IProjectManager>(TYPES.Viewer.ProjectManager);
  const { isDesktop } = useResponsive();
  const projectName = useProjectsStore((state) => state.project.currentProject?.name);
  const useViewerStore = useInjection<IViewerStoreInjector>(TYPES.Store.Viewer);
  const isProjectSharable = useViewerStore((state) => state.isProjectSharable);
  const [approvalAction, setApprovalAction] = useState<ProjectAction | null>(null);

  function handleLogoClick() {
    projectManager.quitViewer();
  }

  return (
    <>
      <StyledTopNav direction="left" aria-label="Top Left Navigation" visible={visible}>
        <StyledFlexBox>
          <Panel widthVariant="fullWidth">
            <Panel.Content>
              <Logo title={t('tool.project.exit_message')} onClick={handleLogoClick} />
            </Panel.Content>
            {isDesktop && (
              <>
                <Panel.Content>
                  <ThemeModeSwitch type="icon" />
                </Panel.Content>
                <Panel.Content>
                  <Text size="Body" truncate maxWidth={74} showTooltip>
                    {projectName ?? <Skeleton.Input active />}
                  </Text>
                </Panel.Content>
              </>
            )}
          </Panel>
        </StyledFlexBox>
      </StyledTopNav>

      <StyledTopNav direction="right" aria-label="Top Right Navigation" visible={visible}>
        <StyledFlexBox>
          <Panel widthVariant="fullWidth">
            {isProjectSharable && (
              <Panel.Content>
                <Share onRequestApproval={() => setApprovalAction(ProjectAction.Share)} />
              </Panel.Content>
            )}
            {isDesktop && <ProjectActions type="desktop" />}
            {!isDesktop && (
              <Panel.Content>
                <BottomSheetMenu />
              </Panel.Content>
            )}
          </Panel>
        </StyledFlexBox>
      </StyledTopNav>
      <AdminApprovalContainer action={approvalAction} onClose={() => setApprovalAction(null)} />
    </>
  );
};
